import type { AxiosError } from 'axios'

/**
 * Sub-piece C — self-healing onboarding-status logic.
 *
 * A freshly-approved (or still-pending) self-serve admin who lands on the SPA
 * during the pre-allowlist window gets a 403 from `getCurrentUser()` — the
 * Function App's org-allowlist gate (`InCodeAuthMiddleware` →
 * `IsTenantAllowedAsync`) denies any tid with no active `Organizations` row.
 * That gate's *deny* verdict is cached for only 1 minute
 * (`AllowlistNegativeCacheTtl`), so once an admin approves the org the 403
 * clears on its own within ≤1 min — the cross-process cache cannot be
 * invalidated remotely and deliberately isn't (same trade-off the codebase
 * already made for the PlatformAdmin grant cache).
 *
 * The real defect was never the cache — it was the SPA permanently stranding
 * the admin on a dead-end page. This module holds the pure decision logic for
 * the replacement self-healing page so it can be unit-tested without rendering
 * (the web harness renders with `renderToStaticMarkup`, so effects never run).
 */

/**
 * Outcome of one `getCurrentUser()` poll while the admin waits.
 *
 * - `active`    — the call succeeded; the tenant is now allowlisted. Forward
 *                 the admin into the app.
 * - `pending`   — 403 from the org-allowlist gate. The tenant has no active
 *                 `Organizations` row YET: either still awaiting approval, or
 *                 approved <1 min ago and the Function App's negative allowlist
 *                 cache has not expired. Keep waiting.
 * - `transient` — anything else (5xx, network failure, non-axios). Not a
 *                 verdict on onboarding state; just retry.
 *
 * 401 is intentionally not a distinct case here: the axios response interceptor
 * owns it (silent refresh → retry → logout), so a 401 never settles back to
 * this classifier as something the page acts on. If one ever does, it lands in
 * `transient` and we simply retry.
 */
export type OnboardingProbe = 'active' | 'pending' | 'transient'

function axiosStatus(err: unknown): number | undefined {
  // Duck-type rather than `axios.isAxiosError` so unit tests can pass plain
  // literals (matches the `getServerMessage` convention in api-error.ts).
  if (err && typeof err === 'object' && 'isAxiosError' in err) {
    return (err as AxiosError).response?.status
  }
  return undefined
}

/**
 * Classify a *rejected* `getCurrentUser()` poll. A resolved poll is `active`
 * and handled by the caller directly — this only sees the failure path, so it
 * never returns `active`.
 */
export function classifyProbeError(err: unknown): Exclude<OnboardingProbe, 'active'> {
  return axiosStatus(err) === 403 ? 'pending' : 'transient'
}

/**
 * Fast initial polls before the page drops from the upbeat "finishing setup"
 * state to the calmer "we're reviewing your account" state. 12 × 5s ≈ 60s,
 * which comfortably covers the ≤1-min allowlist self-heal for an admin whose
 * org was approved moments ago.
 */
export const FAST_POLL_ATTEMPTS = 12
/** Delay between fast initial polls (ms). */
export const FAST_POLL_DELAY_MS = 5_000
/**
 * Slow background cadence (ms) once the waiting state is shown, so an admin who
 * leaves the tab open is still auto-forwarded after a later approval — without
 * hammering the gate.
 */
export const SLOW_POLL_DELAY_MS = 30_000

/** Delay before the next poll, given how many polls have already completed. */
export function nextPollDelayMs(attemptsCompleted: number): number {
  return attemptsCompleted < FAST_POLL_ATTEMPTS ? FAST_POLL_DELAY_MS : SLOW_POLL_DELAY_MS
}

/**
 * Whether to show the calmer "we're reviewing your account" copy (true) vs the
 * initial "we're finishing your setup" spinner (false), given completed polls.
 */
export function shouldShowWaiting(attemptsCompleted: number): boolean {
  return attemptsCompleted >= FAST_POLL_ATTEMPTS
}
